// src/pages/Campaigns/EventPost/EventPostManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, FormControl, InputLabel, Select, MenuItem, Grid,
  Alert, Pagination, CircularProgress, Stack, Card, CardContent, Tooltip,
  InputAdornment, TextField, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Visibility as ViewIcon,
  Category as CategoryIcon, Code as CodeIcon,
  Publish as PublishIcon, Schedule as ScheduleIcon,
  Archive as ArchiveIcon, ModelTraining as TrainIcon,
  RemoveCircleOutline as UntrainIcon, Article as ArticleIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';

const EventPostManagement = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Categories
  const [categories, setCategories] = useState([]);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Selection for bulk operations
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [isTraining, setIsTraining] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, post: null });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar({ ...snackbar, open: false }), 3000);
  };

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const viewPerm = await hasPermission('volunteer_management', 'view');
        const modifyPerm = await hasPermission('volunteer_management', 'add|edit');
        const deletePerm = await hasPermission('volunteer_management', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView && chatbotId) {
      loadCategories();
    }
  }, [canView, chatbotId]);

  useEffect(() => {
    if (canView && chatbotId) {
      const timeoutId = setTimeout(() => {
        loadPosts();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [canView, chatbotId, page, statusFilter, categoryFilter, searchTerm]);

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get('/api/event-post-categories', {
        params: { chatbot_id: chatbotId }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = {
        chatbot_id: chatbotId,
        page,
        per_page: 10
      };

      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/api/event-posts', { params });

      setPosts(response.data.posts || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotal(response.data.pagination?.total || 0);
      setSelectedPosts([]);
    } catch (error) {
      showSnackbar('Error loading posts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPosts(posts.map(p => p.id));
    } else {
      setSelectedPosts([]);
    }
  };

  const handleSelectPost = (postId) => {
    setSelectedPosts(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  };

  const handlePublish = async (postId) => {
    try {
      await axiosInstance.post(`/api/event-posts/${postId}/publish`, null, {
        params: { chatbot_id: chatbotId }
      });
      showSnackbar('Post published successfully', 'success');
      loadPosts();
    } catch (error) {
      showSnackbar('Error publishing post', 'error');
    }
  };

  const handleArchive = async (postId) => {
    try {
      await axiosInstance.post(`/api/event-posts/${postId}/archive`, null, {
        params: { chatbot_id: chatbotId }
      });
      showSnackbar('Post archived successfully', 'success');
      loadPosts();
    } catch (error) {
      showSnackbar('Error archiving post', 'error');
    }
  };

  const handleTrain = async (postId) => {
    setIsTraining(true);
    try {
      await axiosInstance.post(`/api/event-posts/${postId}/train`, null, {
        params: { chatbot_id: chatbotId }
      });
      showSnackbar('Post trained for chatbot successfully', 'success');
      loadPosts();
    } catch (error) {
      showSnackbar('Error training post', 'error');
    } finally {
      setIsTraining(false);
    }
  };

  const handleUntrain = async (postId) => {
    try {
      await axiosInstance.post(`/api/event-posts/${postId}/untrain`, null, {
        params: { chatbot_id: chatbotId }
      });
      showSnackbar('Post removed from chatbot training', 'success');
      loadPosts();
    } catch (error) {
      showSnackbar('Error untraining post', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.post) return;

    try {
      await axiosInstance.delete(`/api/event-posts/${deleteDialog.post.id}`, {
        params: { chatbot_id: chatbotId }
      });
      showSnackbar('Post deleted successfully', 'success');
      setDeleteDialog({ open: false, post: null });
      loadPosts();
    } catch (error) {
      showSnackbar('Error deleting post', 'error');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'default', label: 'Draft' },
      PUBLISHED: { color: 'success', label: 'Published' },
      SCHEDULED: { color: 'info', label: 'Scheduled' },
      ARCHIVED: { color: 'warning', label: 'Archived' }
    };
    const config = statusConfig[status] || statusConfig.DRAFT;
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You don't have permission to view event posts.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Snackbar */}
      {snackbar.open && (
        <Alert severity={snackbar.severity} sx={{ mb: 2 }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArticleIcon fontSize="large" /> Event Posts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage blog posts about your events
          </Typography>
        </Box>

        {canModify && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts/categories`)}
            >
              Categories
            </Button>
            <Button
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts/api-help`)}
            >
              API Integration
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts/new`)}
            >
              Create Post
            </Button>
          </Stack>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4">{total}</Typography>
              <Typography color="text.secondary">Total Posts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {posts.filter(p => p.status === 'PUBLISHED').length}
              </Typography>
              <Typography color="text.secondary">Published</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {posts.filter(p => p.status === 'DRAFT').length}
              </Typography>
              <Typography color="text.secondary">Drafts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {posts.filter(p => p.is_trained).length}
              </Typography>
              <Typography color="text.secondary">Trained</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="PUBLISHED">Published</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="ARCHIVED">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button onClick={() => { setStatusFilter(''); setCategoryFilter(''); setSearchTerm(''); }}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Posts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedPosts.length > 0 && selectedPosts.length < posts.length}
                  checked={posts.length > 0 && selectedPosts.length === posts.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Categories</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Trained</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No posts found</Typography>
                  {canModify && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts/new`)}
                    >
                      Create Your First Post
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              posts.map(post => (
                <TableRow key={post.id} hover selected={selectedPosts.includes(post.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedPosts.includes(post.id)}
                      onChange={() => handleSelectPost(post.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {post.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.post_identity}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(post.status)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {post.categories?.slice(0, 2).map(cat => (
                        <Chip
                          key={cat.id}
                          size="small"
                          label={cat.name}
                          sx={{ backgroundColor: cat.color, color: '#fff', fontSize: '0.7rem' }}
                        />
                      ))}
                      {post.categories?.length > 2 && (
                        <Chip size="small" label={`+${post.categories.length - 2}`} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(post.published_at)}</TableCell>
                  <TableCell>
                    {post.is_trained ? (
                      <Chip size="small" color="success" label="Trained" icon={<TrainIcon />} />
                    ) : (
                      <Chip size="small" color="default" label="Not Trained" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {canModify && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts/${post.id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {post.status === 'DRAFT' && (
                            <Tooltip title="Publish">
                              <IconButton size="small" color="success" onClick={() => handlePublish(post.id)}>
                                <PublishIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {post.status === 'PUBLISHED' && (
                            <Tooltip title="Archive">
                              <IconButton size="small" color="warning" onClick={() => handleArchive(post.id)}>
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {!post.is_trained && post.status === 'PUBLISHED' && (
                            <Tooltip title="Train for Chatbot">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleTrain(post.id)}
                                disabled={isTraining}
                              >
                                <TrainIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {post.is_trained && (
                            <Tooltip title="Remove from Training">
                              <IconButton size="small" color="warning" onClick={() => handleUntrain(post.id)}>
                                <UntrainIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}

                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, post })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, post: null })}>
        <DialogTitle>Delete Post</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{deleteDialog.post?.title}</strong>"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, post: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventPostManagement;
